import { Link } from "react-router-dom";
import { kindLabel, overnightLabel, TAG_LABELS } from "../../lib/access";
import { formatRange, nextSeason } from "../../lib/season";
import type { Fest } from "../../types";

interface FestCardProps {
  fest: Fest;
  miles?: number;
  compact?: boolean;
  onSelect?: (fest: Fest) => void;
}

const FestCard = ({ fest, miles, compact, onSelect }: FestCardProps) => {
  const season = nextSeason(fest);
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-ray leading-tight">{fest.shortName}</h3>
          <p className="text-xs text-alt-wht/70 mt-0.5">
            {fest.city}, {fest.state}
            {miles != null ? ` · ${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi` : ""}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs font-semibold text-foam">{kindLabel(fest)}</div>
          <div className="text-[10px] text-alt-wht/50">{overnightLabel(fest.overnight)}</div>
        </div>
      </div>
      {!compact && (
        <p className="text-sm text-alt-wht/85 mt-2 line-clamp-3">{fest.whyGo}</p>
      )}
      {season && (
        <p className="text-xs text-alt-wht/60 mt-2">
          {formatRange(season.start, season.end)}
        </p>
      )}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {fest.tags.slice(0, compact ? 3 : 5).map((tag) => (
          <span
            key={tag}
            className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-white/15 text-alt-wht/80"
          >
            {TAG_LABELS[tag]}
          </span>
        ))}
      </div>
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        onClick={() => onSelect(fest)}
        className="w-full text-left rounded-xl bg-white/5 hover:bg-white/10 p-3 transition-colors"
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={`/fests/${fest.id}`}
      className="block rounded-2xl bg-alt-blk text-alt-wht p-5 hover:bg-alt-blk/90 transition-colors h-full"
    >
      {inner}
    </Link>
  );
};

export default FestCard;
